import styled from "styled-components";

/* ─── Page wrapper ─── */
export const TalentPageWrap = styled.div`
  flex: 1;
  background: var(--mp-bodyBackground, #f7f8fc);
`;

/* ─── HERO ─── */
export const TalentHero = styled.section`
  width: 100%;
  padding: 72px 40px 56px;
  max-width: 1680px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 32px 16px 28px;
  }
`;

export const TalentHeroEyebrow = styled.div`
  width: max-content;
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  margin-bottom: 16px;
  color: var(--mp-featureIconColor, #D863BA);
  background: none;
  -webkit-text-fill-color: initial;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

export const TalentHeroHeading = styled.h1`
  margin: 0 0 20px 0;
  font-family: 'Liberation Serif', serif;
  font-weight: 400;
  font-size: 56px;
  line-height: 64px;
  letter-spacing: -1.2px;
  color: var(--mp-headingColor, #171717);
  max-width: 700px;

  span.gradient {
    font-style: italic;
    display: inline-block;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  [data-marketplace-theme="light"] & span.gradient,
  :root:not([data-marketplace-theme="dark"]) & span.gradient {
    background: var(--mp-heroAccent, linear-gradient(95.57deg, #FFD573 0%, #D863BA 100%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

export const TalentHeroSub = styled.p`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 16px;
  line-height: 26px;
  color: var(--mp-featureDescColor, #434655);
  max-width: 540px;
  opacity: 0.85;

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 20px;
  }
`;

export const TalentHeroTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

export const ViewToggle = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
`;

export const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid ${p => p.$active ? "var(--mp-featureIconColor, #D863BA)" : "rgba(23,23,23,0.12)"};
  background: ${p => p.$active ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "transparent"};
  color: ${p => p.$active ? "#ffffff" : "var(--mp-headingColor, #171717)"};
  transition: all 0.2s;

  [data-marketplace-theme="dark"] & {
    border-color: ${p => p.$active ? "transparent" : "rgba(255,255,255,0.12)"};
    color: ${p => p.$active ? "#402D00" : "var(--mp-headingColor, #ffffff)"};
  }

  svg { width: 16px; height: 16px; }
`;

/* ─── FILTER BAR ─── */
export const FilterBar = styled.section`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px 48px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 480px) {
    padding: 0 16px 24px;
    gap: 8px;
  }
`;

export const FilterSelect = styled.div`
  flex: 1;
  max-width: 220px;
  min-width: 160px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23, 23, 23, 0.1);
  cursor: pointer;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--mp-featureDescColor, #434655);
  white-space: nowrap;
  transition: border-color 0.2s;
  box-sizing: border-box;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1a1d21;
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: #b0b3b8;
  }

  &:hover { border-color: var(--mp-featureIconColor, #D863BA); }

  svg { width: 16px; height: 16px; opacity: 0.6; }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 10px;
    gap: 6px;
    max-width: 100%;
  }
`;

export const FilterApplyBtn = styled.button`
  flex: 1;
  max-width: 180px;
  min-width: 140px;
  padding: 12px 24px;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: var(--mp-heroBtnColor, #ffffff);
  transition: opacity 0.2s;
  white-space: nowrap;
  box-sizing: border-box;

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #402D00;
  }

  &:hover { opacity: 0.88; }
`;

/* ─── PROFILE CARDS GRID ─── */
export const ProfilesSection = styled.section`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px 64px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 0 12px 40px;
  }
`;

export const ProfilesGrid = styled.div<{ $viewMode?: "grid" | "list", $isHiringTab?: boolean }>`
  display: grid;
  grid-template-columns: ${p => p.$viewMode === "list" ? "1fr" : "repeat(3, 1fr)"};
  gap: 28px;
  align-items: stretch;
  padding-top: ${p => (p.$viewMode === "list" || p.$isHiringTab) ? "0" : "60px"};

  @media (max-width: 1024px) {
    grid-template-columns: ${p => p.$viewMode === "list" ? "1fr" : "repeat(2, 1fr)"};
    padding-top: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const ProfileCard = styled.div<{ $featured?: boolean, $viewMode?: "grid" | "list", $elevateCenter?: boolean }>`
  border-radius: 28px;
  overflow: hidden;
  background: var(--mp-cardBackground, #ffffff);
  border: 1px solid rgba(23,23,23,0.08);
  display: flex;
  flex-direction: ${p => p.$viewMode === "list" ? "row" : "column"};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border: 1px solid rgba(255,255,255,0.08);
  }

  ${p => p.$viewMode !== "list" && p.$elevateCenter !== false && `
    &:nth-child(3n + 2) {
      transform: translateY(-56px);

      &:hover {
        transform: translateY(-64px);
        box-shadow: 0 20px 48px rgba(0,0,0,0.22);
      }
    }
  `}

  &:hover {
    transform: ${p => p.$viewMode === "list" ? "translateY(-4px)" : p.$elevateCenter !== false ? "translateY(-6px)" : "translateY(-4px)"};
    box-shadow: 0 20px 48px rgba(0,0,0,0.16);
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    transform: none !important;
    &:hover {
      transform: translateY(-4px) !important;
    }
  }
`;

export const ProfileCardImageWrap = styled.div<{ $viewMode?: "grid" | "list" }>`
  width: ${p => p.$viewMode === "list" ? "300px" : "100%"};
  aspect-ratio: ${p => p.$viewMode === "list" ? "1" : "4 / 3.2"};
  position: relative;
  overflow: hidden;
  background: var(--mp-cardBackground, #f0f0f0);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    aspect-ratio: 4 / 3.2;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1e2226;
  }
`;

export const ProfileCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  transition: transform 0.4s ease;

  ${ProfileCard}:hover & {
    transform: scale(1.04);
  }
`;

export const ProfileCardImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%);
`;

export const ProfileCardBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(8px);
  color: #ffffff;
  border: 1px solid rgba(255,255,255,0.3);
`;

export const ProfileCardInnerOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
`;

export const ProfileCardRole = styled.div`
  width: max-content;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 4px;
  color: #ffffff;
  opacity: 0.75;
`;

export const ProfileCardName = styled.h3`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 28px;
  color: #ffffff;
`;

export const ProfileCardBody = styled.div`
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  justify-content: center;
`;

export const ProfileSkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ProfileSkillTag = styled.span`
  padding: 4px 10px;
  border-radius: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: rgba(23,23,23,0.06);
  color: var(--mp-featureDescColor, #434655);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(255,255,255,0.07);
    color: #b0b3b8;
  }
`;

export const ProfileMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

export const ProfileMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ProfileMetaLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.6;
`;

export const ProfileMetaValue = styled.span`
  font-family: 'Liberation Serif', serif;
  font-size: 15px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);

  span.accent {
    display: inline-block;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    [data-marketplace-theme="light"] &,
    :root:not([data-marketplace-theme="dark"]) & {
      background: none;
      -webkit-text-fill-color: initial;
      color: var(--mp-featureIconColor, #D863BA);
    }
  }
`;

export const PortfolioPreviewLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.6;
`;

export const PortfolioPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

export const PortfolioThumb = styled.div`
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(23,23,23,0.08);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(255,255,255,0.06);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ViewDossierBtn = styled.button<{ $viewMode?: "grid" | "list" }>`
  width: ${p => p.$viewMode === "list" ? "max-content" : "100%"};
  padding: 12px ${p => p.$viewMode === "list" ? "32px" : "12px"};
  border-radius: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: var(--mp-heroBtnColor, #ffffff);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    color: #402D00;
  }

  &:hover { opacity: 0.88; }
`;

/* ─── DISCOVER MORE ─── */
export const DiscoverMoreWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding-bottom: 80px;
`;

export const DiscoverMoreBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border-radius: 50px;
  border: 1px solid rgba(23,23,23,0.15);
  background: transparent;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-headingColor, #171717);
  cursor: pointer;
  transition: all 0.2s;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.15);
    color: var(--mp-headingColor, #ffffff);
  }

  &:hover {
    border-color: var(--mp-featureIconColor, #D863BA);
    color: var(--mp-featureIconColor, #D863BA);
  }

  [data-marketplace-theme="dark"] &:hover,
  .dark &:hover {
    border-color: #FFC951;
    color: #FFC951;
  }

  svg { width: 16px; height: 16px; }
`;

/* ─── GLOBAL REACH ─── */
export const GlobalReachSection = styled.section`
  width: 100%;
  background: var(--mp-cardBackground, #ffffff);
  border-top: 1px solid rgba(23,23,23,0.06);
  border-bottom: 1px solid rgba(23,23,23,0.06);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #0f1114;
    border-color: rgba(255,255,255,0.05);
  }
`;

export const GlobalReachInner = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 80px 40px;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  @media (max-width: 480px) {
    padding: 40px 16px;
    gap: 32px;
  }
`;

export const GlobalReachLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const GlobalReachHeading = styled.h2`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 56px;
  line-height: 64px;
  color: var(--mp-headingColor, #171717);

  span.accent {
    display: block;
    font-style: normal;
    width: max-content;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;

    [data-marketplace-theme="light"] &,
    :root:not([data-marketplace-theme="dark"]) & {
      background: var(--mp-heroAccent, linear-gradient(95.57deg, #FFD573 0%, #D863BA 100%));
      -webkit-background-clip: text;
    }
  }

  @media (max-width: 768px) {
    font-size: 40px;
    line-height: 48px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
    line-height: 34px;
  }
`;

export const GlobalReachDesc = styled.p`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 22px;
  line-height: 32px;
  color: var(--mp-featureDescColor, #434655);
  max-width: 580px;
  opacity: 0.9;

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

export const GlobalReachStats = styled.div`
  display: flex;
  gap: 80px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 32px;
  }
`;

export const GlobalReachStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GlobalReachStatNum = styled.span`
  font-family: 'Liberation Serif', serif;
  font-weight: 700;
  font-size: 28px;
  color: var(--mp-headingColor, #171717);
`;

export const GlobalReachStatLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.65;
`;

export const GlobalReachMapLink = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--mp-featureIconColor, #D863BA);
  margin-top: 16px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    width: max-content;
  }

  &:hover { opacity: 0.8; }
`;

export const GlobalReachMapWrap = styled.div`
  width: 100%;

  img {
    width: 100%;
    display: block;
    object-fit: cover;
  }
`;
